/**
 * The store contract every React-facing piece of engine state implements.
 *
 * It maps directly onto `useSyncExternalStore(subscribe, getSnapshot)`:
 * - `getSnapshot()` returns an immutable value whose identity is stable until
 *   the state actually changes (review F-3). React compares by reference.
 * - `subscribe()` listeners are notified through the ChangeScheduler, so they
 *   fire once per command batch.
 */
import type { ChangeScheduler } from "./ChangeScheduler";
import type { Unsubscribe } from "./Emitter";

export interface ReadableStore<T> {
  // Function properties, not methods: implementations must be bound so they can
  // be handed straight to useSyncExternalStore without losing `this`.
  readonly subscribe: (listener: () => void) => Unsubscribe;
  readonly getSnapshot: () => T;
}

/** A store holding one immutable snapshot plus a monotonically increasing version. */
export class VersionedStore<T> implements ReadableStore<T> {
  private snapshot: T;
  private versionCounter = 0;
  private readonly listeners = new Set<() => void>();

  constructor(
    private readonly scheduler: ChangeScheduler,
    initial: T,
  ) {
    this.snapshot = initial;
  }

  get version(): number {
    return this.versionCounter;
  }

  readonly subscribe = (listener: () => void): Unsubscribe => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  readonly getSnapshot = (): T => this.snapshot;

  /** Replaces the snapshot. Callers must pass a new object; mutation in place is never allowed. */
  publish(next: T): void {
    this.snapshot = next;
    this.versionCounter++;
    this.scheduler.schedule(this.notify);
  }

  // A stable function identity lets the scheduler de-duplicate notifications per batch.
  private readonly notify = (): void => {
    for (const listener of [...this.listeners]) listener();
  };
}
